// partnerships/AddPartnershipModal.tsx
import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, DollarSign, User, Mail, Phone, Globe, Tag, FileText, CheckCircle, ChevronLeft, ChevronRight, Briefcase, CheckSquare } from 'lucide-react';
import { Partnership, CreatePartnershipData, usePartnershipsStore } from '../../store/partnershipsStore';

interface AddPartnershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  partnership?: Partnership | null;
}

const AddPartnershipModal: React.FC<AddPartnershipModalProps> = ({
  isOpen,
  onClose,
  partnership
}) => {
  const { createPartnership, updatePartnership } = usePartnershipsStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<CreatePartnershipData>({
    brand: '',
    contact: '',
    email: '',
    stage: 'Lead',
    value: 0,
    deliveryDate: '',
    notes: '',
    tags: [],
    priority: 'medium',
    phone: '',
    website: '',
    socialMedia: {
      instagram: '',
      twitter: '',
      linkedin: '',
      tiktok: '',
    },
    campaignType: '',
    campaignDescription: '',
    deliverables: [{ contentType: '', title: '', description: '', deadline: '' }],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const steps = [
    { number: 1, title: 'Basic Info', icon: User },
    { number: 2, title: 'Details', icon: Briefcase },
    { number: 3, title: 'Deliverables', icon: CheckSquare },
    { number: 4, title: 'Notes', icon: FileText },
  ];

  const contentTypes = [
    'Video',
    'Photo Post',
    'Story',
    'Carousel',
    'Reel / Short Video',
    'Long Video',
    'Blog / Article',
    'UGC (Brand can reuse)'
  ];

  const stages = [
    'Inbound Request',
    'Lead',
    'Pitch Sent',
    'Contract Sent',
    'Content Delivered',
    'Payment Received'
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400' },
    { value: 'high', label: 'High', color: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400' }
  ];

  const campaignTypes = [
    'Product Promotion',
    'Brand Awareness',
    'Seasonal Campaign',
    'Offer / Discount Campaign',
    'New Launch Campaign',
    'Review / Testimonial',
    'Giveaway',
    'Event / Store Visit',
    'Affiliate / Performance',
    'UGC Creation Only'
  ];

  // Initialize form with partnership data if editing
  useEffect(() => {
    if (partnership) {
      setFormData({
        brand: partnership.brand || '',
        contact: partnership.contact || '',
        email: partnership.email || '',
        stage: partnership.stage || 'Lead',
        value: partnership.value || 0,
        deliveryDate: partnership.deliveryDate ? new Date(partnership.deliveryDate).toISOString().split('T')[0] : '',
        notes: partnership.notes || '',
        tags: partnership.tags || [],
        priority: partnership.priority || 'medium',
        phone: partnership.phone || '',
        website: partnership.website || '',
        socialMedia: partnership.socialMedia || {
          instagram: '',
          twitter: '',
          linkedin: '',
          tiktok: '',
        },
        campaignType: partnership.campaignType || '',
        campaignDescription: partnership.campaignDescription || '',
        deliverables: partnership.deliverables && partnership.deliverables.length > 0
          ? partnership.deliverables
          : [{ contentType: '', title: '', description: '', deadline: '' }],
      });

      setTagInput(partnership.tags?.join(', ') || '');
    }
  }, [partnership]);

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.brand.trim()) newErrors.brand = 'Brand name is required';
      if (!formData.contact.trim()) newErrors.contact = 'Contact name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (step === 2) {
      if (!formData.value || formData.value <= 0) newErrors.value = 'Partnership value is required';
      if (!formData.stage) newErrors.stage = 'Stage is required';
      if (!formData.campaignType?.trim()) newErrors.campaignType = 'Campaign type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setErrors({});
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSocialMediaChange = (platform: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [platform]: value
      }
    }));
  };

  const [tagInput, setTagInput] = useState(formData.tags?.join(", ") || "");

  const handleTagsChange = (value: string) => {
    setTagInput(value);
    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
    setFormData(prev => ({
      ...prev,
      tags
    }));
  };



  const handleDeliverableChange = (index: number, field: 'contentType' | 'title' | 'description' | 'deadline', value: string) => {
    const newDeliverables = [...(formData.deliverables || [{ contentType: '', title: '', description: '', deadline: '' }])];
    newDeliverables[index] = {
      ...newDeliverables[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      deliverables: newDeliverables
    }));
  };

  const addDeliverable = () => {
    setFormData(prev => ({
      ...prev,
      deliverables: [...(prev.deliverables || []), { contentType: '', title: '', description: '', deadline: '' }]
    }));
  };

  const removeDeliverable = (index: number) => {
    const newDeliverables = formData.deliverables?.filter((_, i) => i !== index) || [];
    setFormData(prev => ({
      ...prev,
      deliverables: newDeliverables.length > 0 ? newDeliverables : [{ contentType: '', title: '', description: '', deadline: '' }]
    }));
  };

  const handleSubmit = async () => {
    if (!validateStep(1) || !validateStep(2)) {
      setCurrentStep(1);
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for API - convert deliveryDate to ISO format if it exists
      const apiData = {
        ...formData,
        deliveryDate: formData.deliveryDate ? new Date(formData.deliveryDate).toISOString() : undefined
      };

      if (partnership) {
        await updatePartnership({ ...apiData, id: partnership.id });
      } else {
        await createPartnership(apiData);
      }

      // Only close modal on success
      onClose();

      // Reset form
      setFormData({
        brand: '',
        contact: '',
        email: '',
        stage: 'Lead',
        value: 0,
        deliveryDate: '',
        notes: '',
        tags: [],
        priority: 'medium',
        phone: '',
        website: '',
        socialMedia: { instagram: '', twitter: '', linkedin: '', tiktok: '' },
        campaignType: '',
        campaignDescription: '',
        deliverables: [{ contentType: '', title: '', description: '', deadline: '' }],
      });
      setCurrentStep(1);
      setErrors({});
    } catch (error) {
      console.error('Failed to save partnership:', error);
      // Don't close modal on error - let user see the error and try again
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {partnership ? 'Edit Partnership' : 'Add New Partnership'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Step Indicator */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <button
                    onClick={() => setCurrentStep(step.number)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${currentStep === step.number
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                      : currentStep > step.number
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-400 dark:text-gray-500'
                      }`}
                  >
                    <step.icon className="w-4 h-4" />
                    <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
                  </button>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.number
                      ? 'bg-green-500'
                      : 'bg-gray-200 dark:bg-gray-700'
                      }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[50vh]">
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Brand Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.brand}
                      onChange={(e) => handleInputChange('brand', e.target.value)}
                      className={`w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${errors.brand ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-600'
                        }`}
                      placeholder="e.g. TechFlow Solutions"
                    />
                    {errors.brand && <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.brand}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Contact Person <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.contact}
                      onChange={(e) => handleInputChange('contact', e.target.value)}
                      className={`w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${errors.contact ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-600'
                        }`}
                      placeholder="e.g. Sarah Johnson"
                    />
                    {errors.contact && <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.contact}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${errors.email ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-600'
                          }`}
                        placeholder="sarah@techflow.com"
                      />
                    </div>
                    {errors.email && <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="tel"
                        value={formData.phone || ''}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Website
                    </label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="url"
                        value={formData.website || ''}
                        onChange={(e) => handleInputChange('website', e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Social Media Links */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Social Media Profiles
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={formData.socialMedia?.instagram || ''}
                      onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                      className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-sm"
                      placeholder="Instagram username"
                    />
                    <input
                      type="text"
                      value={formData.socialMedia?.twitter || ''}
                      onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
                      className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-sm"
                      placeholder="Twitter handle"
                    />
                    <input
                      type="text"
                      value={formData.socialMedia?.linkedin || ''}
                      onChange={(e) => handleSocialMediaChange('linkedin', e.target.value)}
                      className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-sm"
                      placeholder="LinkedIn profile"
                    />
                    <input
                      type="text"
                      value={formData.socialMedia?.tiktok || ''}
                      onChange={(e) => handleSocialMediaChange('tiktok', e.target.value)}
                      className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-sm"
                      placeholder="TikTok username"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Partnership Details */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Partnership Value <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="number"
                        min="0"
                        value={formData.value}
                        onChange={(e) => handleInputChange('value', Number(e.target.value))}
                        className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${errors.value ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-600'
                          }`}
                        placeholder="2500"
                      />
                    </div>
                    {errors.value && <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.value}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Current Stage <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.stage}
                      onChange={(e) => handleInputChange('stage', e.target.value as any)}
                      className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                    >
                      {stages.map(stage => (
                        <option key={stage} value={stage}>{stage}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Priority Level
                    </label>
                    <div className="flex gap-2">
                      {priorities.map(priority => (
                        <button
                          key={priority.value}
                          type="button"
                          onClick={() => handleInputChange('priority', priority.value)}
                          className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium transition-colors border ${formData.priority === priority.value
                            ? priority.color + ' border-current'
                            : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                            }`}
                        >
                          {priority.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tags
                    </label>
                    <div className="relative">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => handleTagsChange(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                        placeholder="tech, software, B2B"
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Separate tags with commas</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Campaign Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.campaignType || ''}
                      onChange={(e) => handleInputChange('campaignType', e.target.value)}
                      className={`w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ${errors.campaignType ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'}`}
                    >
                      <option value="">Select campaign type</option>
                      {campaignTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {errors.campaignType && <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.campaignType}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Campaign Description
                    </label>
                    <textarea
                      value={formData.campaignDescription || ''}
                      onChange={(e) => handleInputChange('campaignDescription', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                      placeholder="Brief description of the campaign..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Deliverables */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Deliverables
                  </label>
                  <button
                    type="button"
                    onClick={addDeliverable}
                    className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                  >
                    <Plus className="w-4 h-4" />
                    Add Deliverable
                  </button>
                </div>
                <div className="space-y-4">
                  {formData.deliverables?.map((deliverable, index) => (
                    <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Deliverable {index + 1}
                        </span>
                        {formData.deliverables && formData.deliverables.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeDeliverable(index)}
                            className="p-1 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="space-y-3">
                        {/* Row 1: Content Type + Deadline */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Content Type</label>
                            <select
                              value={deliverable.contentType}
                              onChange={(e) => handleDeliverableChange(index, 'contentType', e.target.value)}
                              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            >
                              <option value="">Select type...</option>
                              {contentTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Deadline</label>
                            <input
                              type="date"
                              value={deliverable.deadline || ''}
                              onChange={(e) => handleDeliverableChange(index, 'deadline', e.target.value)}
                              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            />
                          </div>
                        </div>
                        {/* Row 2: Title */}
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Title</label>
                          <input
                            type="text"
                            value={deliverable.title}
                            onChange={(e) => handleDeliverableChange(index, 'title', e.target.value)}
                            className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            placeholder="e.g., Product Review Video"
                          />
                        </div>
                        {/* Row 3: Description */}
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Description (optional)</label>
                          <textarea
                            value={deliverable.description || ''}
                            onChange={(e) => handleDeliverableChange(index, 'description', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                            placeholder="Specific requirements for this deliverable..."
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Additional Notes */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Partnership Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    rows={6}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                    placeholder="Add any additional notes about this partnership, communication history, special requirements, or other relevant details..."
                  />
                </div>

                {/* Summary */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <h5 className="font-medium text-gray-900 dark:text-white mb-3">Partnership Summary</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Brand:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">{formData.brand}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Contact:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">{formData.contact}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Value:</span>
                      <span className="ml-2 font-medium text-green-600 dark:text-green-400">${formData.value.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Stage:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">{formData.stage}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Priority:</span>
                      <span className="ml-2 font-medium capitalize text-gray-900 dark:text-white">{formData.priority}</span>
                    </div>
                    {formData.deliveryDate && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Delivery Date:</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-white">
                          {new Date(formData.deliveryDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <button
              type="button"
              onClick={() => currentStep > 1 ? handleBack() : onClose()}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              disabled={isSubmitting}
            >
              <ChevronLeft className="w-4 h-4" />
              {currentStep > 1 ? 'Back' : 'Cancel'}
            </button>

            {currentStep < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {partnership ? 'Update Partnership' : 'Create Partnership'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPartnershipModal;